import { trapFocus } from '../theme-scripts.js';
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.stickyHeader = document.querySelector('.header-wrapper');
    // Get and set header height
    this.headerHeight = `${this.stickyHeader.querySelector('.header-wrapper > header').offsetHeight}px`;

    // Enables the transparent header if set to 'true'
    if (getComputedStyle(document.body).getPropertyValue('--page-header-transparent') == 'true') {
      this.stickyHeader.setAttribute('data-transparent-header', 'true');
    } else if (getComputedStyle(document.body).getPropertyValue('--header-transparent-on-hero-element') == 'true') {
      this.stickyHeader.setAttribute('data-transparent-header', 'true');
    } else {
      this.stickyHeader.setAttribute('data-transparent-header', 'false');
    }

    // Enable transition after setting the initial state, so the background doesn't
    // animate on first render (which would cause a flash of wrong text/bg combination)
    requestAnimationFrame(() => {
      this.stickyHeader.classList.add('header-initialized');
    });

    // Registre all details and add on click event
    this.loadMegamenuDropdown();
  }

  loadMegamenuDropdown() {
    // Reset values
    this.backgroundAnimation = null;
    this.isClosing = false;
    this.isExpanding = false;

    // Find all top navigation items in mega menu
    this.details = this.querySelectorAll('details.mega-menu');

    // Loop throguht all top navigation items
    this.details.forEach((element) => {
      // Add event listener 'mouseenter' on each megamenu item to open on hover
      element.addEventListener('mouseenter', () => {
        // Already open — don't hide and re-trigger (can fire when mouse moves from summary
        // into the absolutely-positioned content below, briefly leaving the layout box)
        if (this.activeDetails === element && element.open) return;

        // Instantly close previously open item when switching between items
        if (this.activeDetails && this.activeDetails !== element) {
          if (this.backgroundAnimation) {
            this.backgroundAnimation.cancel();
            this.backgroundAnimation = null;
          }
          this.activeDetails.open = false;
          const prevList = this.activeDetails.querySelector('.mega-menu__list');
          if (prevList) {
            prevList.classList.add('tw-invisible', 'tw-opacity-0');
            prevList.classList.remove('tw-opacity-100');
          }
          this.isClosing = false;
          this.isExpanding = false;
        }

        this.activeDetails = element;
        this.textHideMegamenuContent();

        if (!element.open) {
          this.openMegamenuContent();
        }
      });

      element.closest('.header-wrapper').addEventListener('mouseenter', (event) => {
        event.currentTarget.classList.add('expanded');
      });

      // Don't close if the mouse is moving into the mega-menu content (which sits below the header-wrapper)
      element.closest('.header-wrapper').addEventListener('mouseleave', (event) => {
        if (event.relatedTarget && event.relatedTarget.closest('.mega-menu__content')) {
          return;
        }
        event.currentTarget.classList.remove('expanded');
        if (element.open && this.activeDetails) {
          this.shrinkMegamenuContent();
        }
      });

      // Close when the mouse leaves the mega-menu content itself (unless returning to the header)
      const megaMenuContent = element.querySelector('.mega-menu__content');
      if (megaMenuContent) {
        megaMenuContent.addEventListener('mouseleave', (event) => {
          if (event.relatedTarget && event.relatedTarget.closest('.header-wrapper')) {
            return;
          }
          element.closest('.header-wrapper').classList.remove('expanded');
          if (element.open && this.activeDetails) {
            this.shrinkMegamenuContent();
          }
        });
      }

    });

    // Close open mega menu when hovering nav items that don't have a mega menu
    if (this.details.length > 0) {
      const navList = this.details[0].closest('ul');
      if (navList) {
        navList.querySelectorAll(':scope > li').forEach((li) => {
          if (!li.querySelector('details.mega-menu')) {
            li.addEventListener('mouseenter', () => {
              if (this.activeDetails && this.activeDetails.open) {
                const headerWrapper = this.activeDetails.closest('.header-wrapper');
                if (headerWrapper) headerWrapper.classList.remove('expanded');
                this.shrinkMegamenuContent();
              }
            });
          }
        });
      }
    }

    // Close open mega menu when hovering header__menu-items outside any mega-menu
    // (e.g. search, account, currency selector in the secondary menu area)
    this.querySelectorAll('.header__menu-item').forEach((item) => {
      if (!item.closest('details.mega-menu')) {
        item.addEventListener('mouseenter', () => {
          if (this.activeDetails && this.activeDetails.open) {
            const headerWrapper = this.activeDetails.closest('.header-wrapper');
            if (headerWrapper) headerWrapper.classList.remove('expanded');
            this.shrinkMegamenuContent();
          }
        });
      }
    });
  }

  megamenuContentOnClick() {
    // If close animation is running, or is the current element not [open] we open it, else If open animation is running or current element is [open]
    if (this.isClosing || !this.activeDetails.open) {
      this.openMegamenuContent();
    } else if (this.isExpanding || this.activeDetails.open) {
      this.shrinkMegamenuContent();
    }
  }

  shrinkMegamenuContent() {
    // Set current element we close, to 'true'.
    this.isClosing = true;

    // First we check if an animation is running, then we cancel it
    if (this.backgroundAnimation) {
      this.backgroundAnimation.cancel();
    }

    // Shrink animation
    this.backgroundAnimation = this.activeDetails.querySelector('.mega-menu__background-transition').animate(
      {
        height: [`calc(100% + ${this.headerHeight})`, '0'],
        top: [`-${this.headerHeight}`, '0'],
      },
      {
        duration: 200,
        easing: 'cubic-bezier(.14,.4,.59,.59)',
      },
    );

    // Animation listeners, reset variables then hide content
    (this.backgroundAnimation.onfinish = () => this.onAnimationFinishMegamenuContent(false)), this.textHideMegamenuContent();

    // If animation is cancelled then we reset the variable
    this.backgroundAnimation.oncancel = () => (this.isClosing = false);
  }

  openMegamenuContent() {
    // Set the clicked element to [open]
    this.activeDetails.open = true;

    // Is expanding
    this.isExpanding = true;

    // Start open animation
    window.requestAnimationFrame(() => this.expandMegamenuContent());
  }

  expandMegamenuContent() {
    // First we check if an animation is running, then we cancel it
    if (this.backgroundAnimation) {
      this.backgroundAnimation.cancel();
    }

    // Expand animation
    this.backgroundAnimation = this.activeDetails.querySelector('.mega-menu__background-transition').animate(
      {
        height: ['0', `calc(100% + ${this.headerHeight})`],
        top: ['0', `-${this.headerHeight}`],
      },
      {
        duration: 200,
        easing: 'cubic-bezier(.14,.4,.59,.59)',
      },
    );

    // Animation listeners
    this.backgroundAnimation.onfinish = () => this.textRevealMegamenuContent();
  }

  // Hide all content on the active details based on tailwind classes, opacity is used to smotth out the transition
  textHideMegamenuContent() {
    this.activeDetails.querySelector('.mega-menu__list').classList.add('tw-invisible');
    this.activeDetails.querySelector('.mega-menu__list').classList.add('tw-opacity-0');
    this.activeDetails.querySelector('.mega-menu__list').classList.remove('tw-opacity-100');
  }

  // Reveal all content on the active details based on tailwind classes, opacity is used to smotth out the transition
  textRevealMegamenuContent() {
    this.activeDetails.querySelector('.mega-menu__list').classList.remove('tw-invisible');
    this.activeDetails.querySelector('.mega-menu__list').classList.remove('tw-opacity-0');
    this.activeDetails.querySelector('.mega-menu__list').classList.add('tw-opacity-100');
    trapFocus(this.activeDetails.querySelector('summary').nextElementSibling, this.activeDetails);
  }

  onAnimationFinishMegamenuContent(open) {
    this.activeDetails.open = open;
    this.backgroundAnimation = null;
    this.isClosing = false;
    this.isExpanding = false;
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.stickyHeader = document.querySelector('.header-wrapper');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    // Check if the header should be transparent over the first hero element, then set the variable. This is defined in Theme settings, inside the customizer, and added in 'novicell_settings.liquid'
    this.isheaderTransparentBeforeHero = getComputedStyle(document.body).getPropertyValue('--header-transparent-on-hero-element') == 'true';

    // If 'true' we start IntersectionObserver to check if we are scrolled past the hero element
    if (this.isheaderTransparentBeforeHero) {
      this.headerTransparentBeforeHero();
    }

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  headerTransparentBeforeHero() {
    const heroElement = document.querySelector('.hero-with-slideshow:first-of-type');
    if (heroElement) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.stickyHeader.setAttribute('data-transparent-header', 'true');
            } else {
              this.stickyHeader.setAttribute('data-transparent-header', 'false');
            }
          });
        },
        { threshold: 0.2 },
      );
      observer.observe(heroElement);
    }
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal && !document.body.classList.contains('filter-is-open')) {
        // MENU-692: Altered)
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
    this.moveFilter(false); // MENU-692: Added
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
    this.moveFilter(true); // MENU-692: Added
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    const searchDrawer = document.querySelector('search-drawer');
    if (searchDrawer && searchDrawer.hasAttribute('open')) {
      searchDrawer.close();
      return;
    }
    // Legacy: details-modal based search
    const searchModal = this.header.querySelector('details-modal');
    if (searchModal) searchModal.close(false);
  }

  moveFilter(makeSpaceForHeader) {
    // MENU-692: Added
    // If the sticky header is set to on-scroll-up
    // It should move down an make space for the header, when the header shows on scroll up,
    // and move up again on scroll down
    if (this.getAttribute('data-sticky-type') == 'on-scroll-up') {
      const filterWrapper = document.querySelector('.facets-wrapper');
      const headerHeight = this.offsetHeight;
      if (makeSpaceForHeader) {
        // On scroll up — subtract 2px to offset the .shopify-section-header-sticky `top: -1px` rule
        // and sub-pixel rounding, so the filter sits flush with the visible bottom of the sticky header.
        filterWrapper.style.top = `${headerHeight - 2}px`;
      } else {
        // On scroll down
        filterWrapper.style.top = '0';
      }
    }
  }
}

customElements.define('sticky-header', StickyHeader);
